






































































// Vue
import Vue from "vue";
import { mapGetters } from "vuex";
// moment.js
import moment from "moment";
// Models
import {
  CreateOrUpdateJobDriverModel,
  JobModel,
  JobDriverModel,
  JobDriverStatus,
  JobType
} from "@/core/api/apiClient";
// Services
import JobsService from "@/views/dispatch/jobs/Jobs.service";
// Components
import JobCard from "@/core/components/content/JobCard.vue";
import UpdateJobDialog from "@/views/dispatch/jobs/job-dashboard/_dialogs/UpdateJobDialog.vue";

export default Vue.extend({
  name: "JobDashboard",
  components: { UpdateJobDialog, JobCard },

  data() {
    return {
      isLoading: false,
      refreshJobsTimer: {} as any,

      // Jobs Data
      jobsData: [] as JobModel[],

      // Drivers Data
      driversData: [] as JobDriverModel[]
    };
  },

  mounted() {
    this.getAllActiveJobs();
    this.getAllDrivers();
    this.refreshJobsInterval();
  },

  destroyed() {
    clearInterval(this.refreshJobsTimer);
  },

  methods: {
    getAllActiveJobs(): void {
      this.isLoading = true;

      JobsService.getAllActiveJobs(this.currentUser.clientId).then((data: JobModel[]) => {
        if (!data) return;
        this.jobsData = data;
        this.isLoading = false;
      });
    },

    getAllDrivers(): void {
      JobsService.getAllDrivers(this.currentUser.clientId).then((data: JobDriverModel[]) => {
        if (!data) return;
        this.driversData = data;
      });
    },

    updateJobDrivers(job: JobModel): void {
      if (!job) return;
      if (!job.jobId) return;

      this.isLoading = true;

      // Build payload
      const payload: CreateOrUpdateJobDriverModel[] = job.drivers.map(
        (x) =>
          new CreateOrUpdateJobDriverModel({
            jobDriverId: x.jobDriverId && x.jobDriverId.trim() !== "" ? x.jobDriverId : undefined,
            driverId: x.driverId,
            quarryId: x.quarry?.quarryId ?? undefined
          })
      );
      this.isLoading = true;
      JobsService.createOrUpdateJobDrivers(this.currentUser.clientId, job.jobId, payload)
        .then((response: any) => {
          if (!response) return;

          this.getAllActiveJobs();
          this.updateStore();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    deleteJob(job: JobModel): void {
      if (!job) return;
      if (!job.jobId) return;

      this.isLoading = true;

      JobsService.deleteJob(this.currentUser.clientId, job.jobId)
        .then((response: any) => {
          if (!response) return;

          this.getAllActiveJobs();
          this.updateStore();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    cancelJob(job: JobModel): void {
      if (!job) return;
      if (!job.jobId) return;

      JobsService.cancelJob(this.currentUser.clientId, job)
        .then((response: any) => {
          if (!response) return;

          this.getAllActiveJobs();
          this.updateStore();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    completeJob(job: JobModel): void {
      if (!job) return;
      if (!job.jobId) return;

      this.isLoading = true;

      JobsService.completeJob(this.currentUser.clientId, job)
        .then((response: any) => {
          if (!response) return;

          this.getAllActiveJobs();
          this.updateStore();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateStore(): void {
      this.$store.dispatch("SET_UPDATE_CALENDAR", true);
      this.$store.dispatch("SET_UPDATE_DASHBOARD", true);
      this.$store.dispatch("SET_UPDATE_SCHEDULER", true);
    },

    /**
     * Fetches Jobs every 30 seconds
     * @author Nick Brahimir
     */
    refreshJobsInterval(): void {
      this.refreshJobsTimer = setInterval(() => {
        this.getAllActiveJobs();
      }, 15000);
    },

    /**
     * Resturns the Status for a Driver in the job.Drivers array
     * @author Justin Blackwell & Nick Brahimir
     */
    getIconDriverStatus(jobDrivers: JobDriverModel[], driverId: string): JobDriverStatus | undefined {
      const result = jobDrivers.filter((x) => x.userId === driverId)[0].status;
      return result;
    },

    /**
     * Removes a Driver from the respective array of Drivers on a Job.
     * @author Nick Brahimir
     */
    removeDriver(driverArray: JobDriverModel[], driver: JobDriverModel): JobDriverModel[] {
      const index = driverArray.map((e) => e.driverId).indexOf(driver.driverId);

      if (index >= 0) {
        driverArray.splice(index, 1);
        return driverArray;
      }

      return driverArray;
    },

    hasNotes(job: JobModel): boolean {
      if (job.deliveryDetails) if (job.deliveryDetails.notes) return true;
      if (job.driverNotes) if (job.driverNotes.length > 0) return true;
      return false;
    },

    hasImages(job: JobModel): boolean {
      if (job.jobImages) if (job.jobImages.length > 0) return true;
      return false;
    },

    isDumpJob(job: JobModel): boolean {
      if (job.jobType === JobType.Dump) return true;
      return false;
    },

    isPumpJob(job: JobModel): boolean {
      if (job.jobType === JobType.Pump) return true;
      return false;
    },

    isToday(date: any): boolean {
      // ? Convert the Unix timestamp
      const incomingDate = moment(date.seconds * 1000).startOf("day");
      const dateToday = moment(new Date()).startOf("day");

      return incomingDate.isSame(dateToday);
    },

    isTomorrow(date: any): boolean {
      // ? Convert the Unix timestamp
      const incomingDate = moment(date.seconds * 1000).startOf("day");
      const dateToday = moment(new Date()).startOf("day");

      return incomingDate.isSame(dateToday.add(1, "days"));
    },

    isYesterday(previousJob: JobModel, currentJob: JobModel): boolean {
      // ? Convert the Unix timestamp
      const currentDate = moment(currentJob.deliveryDetails!.deliveryDate).startOf("day");
      const previousDate = moment(previousJob.deliveryDetails!.deliveryDate).startOf("day");

      if (currentDate.isSame(previousDate)) {
        return true;
      }

      return false;
    },

    isRedText(deliveryDateType: string): boolean {
      if (deliveryDateType.toLowerCase() === "not confirmed") return true;
      if (deliveryDateType.toLowerCase() === "will call") return true;
      if (deliveryDateType.toLowerCase() === "anytime") return true;
      if (deliveryDateType.toLowerCase() === "asap") return true;
      return false;
    },

    close(): void {
      this.$emit("close-dashboard");
    }
  },

  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      activeJobs: "activeJobs",
      updateDashboard: "updateDashboard"
    })
  },

  watch: {
    updateDashboard(): void {
      if (this.updateDashboard) {
        this.getAllActiveJobs();
        this.getAllDrivers();
        this.$store.dispatch("SET_UPDATE_DASHBOARD", false);
      }
    }
  },

  filters: {
    moment: function (date: any) {
      try {
        return moment(date.toDate()).format("MMMM Do, YYYY");
      } catch {
        try {
          return moment(date).format("MMMM Do, YYYY");
        } catch {
          return "";
        }
      }
    },

    momentDateTime: function (date: any) {
      try {
        return moment(date.toDate()).format("dddd, MMMM Do YYYY");
      } catch {
        try {
          return moment(date).format("dddd, MMMM Do YYYY");
        } catch {
          return "";
        }
      }
    },

    momentTime: function (date: any) {
      try {
        return moment(date.toDate()).format("h:mm A");
      } catch {
        try {
          return moment(date).format("h:mm A");
        } catch {
          return "";
        }
      }
    }
  }
});
